import React, { useEffect, useState, useRef } from "react";
import {
  Store, User, Mail, Phone, Building, FileText, Briefcase, Tags, Globe,
  Map, MapPin, Camera, X, Calendar, ArrowLeft, ArrowRight, Send, Plus,
  CreditCard, Search, Instagram, Facebook, Link
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useImageCropper } from "../../../hooks/useImageCropper";
import ImageCropModal from "../../../components/common/ImageCropModal";
import {
  fetchCountries,
  fetchStates,
  fetchDistricts,
  fetchLocalAreas,
  fetchBusinessTypes,
  fetchBusinessCategories,
  fetchBusinessServices,
  createInquiry,
} from "../../../features/merchant/inquiryThunks";
import { getMerchantProfile } from "../../../features/merchant/merchantThunks";
import { uploadImageToServer } from "../../../services/imageUpload";
import { toast } from "react-toastify";
import { navigateTo } from "../../../utils/navigation";

const IMAGE_FIELDS = [
  "ownerPhoto",
  "businessLogo",
  "aadhaarCardFront",
  "aadhaarCardBack",
  "panCardFront",
  "panCardBack",
];

// --------------- helpers ---------------
const isEmpty = (val) => !val || !String(val).trim();
const isFutureDate = (date) => new Date(date) > new Date();
const calculateAge = (dob) => {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};
const isValidUrl = (url) => {
  if (!url || !url.trim()) return true;
  try {
    const u = new URL(url);
    return ["http:", "https:"].includes(u.protocol);
  } catch {
    return false;
  }
};
const isValidGSTIN = (g) =>
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(g);
const isValidPAN = (p) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(p);

// max DOB = today minus 18 years
const maxDob = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split("T")[0];
})();

// ----------------------------------------
const MerchantInquiryForm = () => {
  const ownerPhotoCropper      = useImageCropper();
  const businessLogoCropper    = useImageCropper();
  const aadhaarFrontCropper    = useImageCropper();
  const aadhaarBackCropper     = useImageCropper();
  const panFrontCropper        = useImageCropper();
  const panBackCropper         = useImageCropper();
  const dispatch = useDispatch();
  const inquiryState = useSelector((s) => s.inquiry);
  const merchant = useSelector((s) => s.merchant.user);
  const accessToken = useSelector((s) => s.merchant.accessToken);

  const businessTypes = inquiryState.businessTypes?.data || [];
  const businessCategories = inquiryState.businessCategories?.data || [];
  const businessServices = inquiryState.businessServices?.data || [];
  const countries = inquiryState.countries?.data || [];
  const states = inquiryState.states?.data || [];
  const districts = inquiryState.districts?.data || [];
  const localAreas = inquiryState.localAreas?.data || [];

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ---- form state ----
  const [formData, setFormData] = useState({
    // Step 1 – Personal
    fullName: "",
    email: "",
    contactNumber: "",
    dob: "",
    ownerPhoto: null,
    aadhaarNumber: "",
    aadhaarCardFront: null,
    aadhaarCardBack: null,
    panNumber: "",
    panCardFront: null,
    panCardBack: null,
    // Step 2 – Business
    businessName: "",
    gstinNumber: "",
    businessLogo: null,
    businessType: "",
    businessCategory: "",
    businessService: "",
    facebookLink: "",
    instagramLink: "",
    additionalLinks: [""],
    // Step 3 – Address
    country: "",
    state: "",
    district: "",
    localAreaId: "",       // ObjectId when selected from list
    localAreaKeyword: "",  // free-text when typed custom
    pincode: "",
    streetAddress: "",
    agreedToTerms: false,
  });

  const [imagePreview, setImagePreview] = useState({
    ownerPhoto: null,
    businessLogo: null,
    aadhaarCardFront: null,
    aadhaarCardBack: null,
    panCardFront: null,
    panCardBack: null,
  });

  // local area typeahead
  const [localAreaSearch, setLocalAreaSearch] = useState("");
  const [showLocalSuggestions, setShowLocalSuggestions] = useState(false);
  const localAreaRef = useRef(null);

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (localAreaRef.current && !localAreaRef.current.contains(e.target)) {
        setShowLocalSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filteredLocalAreas = localAreas.filter((a) =>
    a.name.toLowerCase().includes(localAreaSearch.toLowerCase())
  );

  // ---- derived ----
  const selectedBusinessType = businessTypes.find(
    (t) => t._id === formData.businessType
  );
  const isServiceProvider =
    selectedBusinessType?.name?.toLowerCase() === "service providers";

  // ---- effects ----
  useEffect(() => {
    dispatch(getMerchantProfile());
    dispatch(fetchCountries());
    dispatch(fetchBusinessTypes());
  }, [dispatch]);

  useEffect(() => {
    if (merchant) {
      setFormData((p) => ({
        ...p,
        fullName: merchant.fullName || "",
        email: merchant.email || "",
        contactNumber: merchant.mobileNumber || "",
      }));
    }
  }, [merchant]);

  useEffect(() => {
    if (!formData.businessType || !selectedBusinessType) return;
    if (isServiceProvider) {
      dispatch(fetchBusinessServices({ businessTypeId: formData.businessType }));
    } else {
      dispatch(fetchBusinessCategories({ businessTypeId: formData.businessType }));
    }
  }, [dispatch, formData.businessType, isServiceProvider, selectedBusinessType]);

  useEffect(() => {
    if (formData.country) dispatch(fetchStates({ countryId: formData.country }));
  }, [dispatch, formData.country]);

  useEffect(() => {
    if (formData.state) dispatch(fetchDistricts({ stateId: formData.state }));
  }, [dispatch, formData.state]);

  useEffect(() => {
    if (formData.district) {
      dispatch(fetchLocalAreas({ districtId: formData.district }));
      // reset local area when district changes
      setLocalAreaSearch("");
      setFormData((p) => ({ ...p, localAreaId: "", localAreaKeyword: "" }));
    }
  }, [dispatch, formData.district]);

  // ---- validation ----
  const validateStep = () => {
    /* Step 1 – Personal */
    if (currentStep === 1) {
      if (!formData.ownerPhoto) {
        toast.error("Owner photo is required");
        return false;
      }
      if (isEmpty(formData.fullName)) {
        toast.error("Name is required");
        return false;
      }
      if (!formData.dob) {
        toast.error("Date of birth is required");
        return false;
      }
      if (isFutureDate(formData.dob)) {
        toast.error("Date of birth cannot be in the future");
        return false;
      }
      if (calculateAge(formData.dob) < 18) {
        toast.error("You must be at least 18 years old");
        return false;
      }
      if (formData.panNumber && !isValidPAN(formData.panNumber)) {
        toast.error("Enter a valid PAN number (e.g., ABCDE1234F)");
        return false;
      }
    }

    /* Step 2 – Business */
    if (currentStep === 2) {
      if (isEmpty(formData.businessName)) {
        toast.error("Business name is required");
        return false;
      }
      if (formData.businessName.trim().length < 3) {
        toast.error("Business name must be at least 3 characters");
        return false;
      }
      if (formData.gstinNumber && !isValidGSTIN(formData.gstinNumber)) {
        toast.error("Enter a valid GSTIN number");
        return false;
      }
      if (!formData.businessType) {
        toast.error("Please select a business type");
        return false;
      }
      if (isServiceProvider && !formData.businessService) {
        toast.error("Please select a business service");
        return false;
      }
      if (!isServiceProvider && !formData.businessCategory) {
        toast.error("Please select a business category");
        return false;
      }
      if (formData.facebookLink && !isValidUrl(formData.facebookLink)) {
        toast.error("Enter a valid Facebook URL");
        return false;
      }
      if (formData.instagramLink && !isValidUrl(formData.instagramLink)) {
        toast.error("Enter a valid Instagram URL");
        return false;
      }
      for (const link of formData.additionalLinks) {
        if (link && !isValidUrl(link)) {
          toast.error("One or more additional social media links are invalid");
          return false;
        }
      }
    }

    /* Step 3 – Address */
    if (currentStep === 3) {
      if (!formData.country) { toast.error("Country is required"); return false; }
      if (!formData.state) { toast.error("State is required"); return false; }
      if (!formData.district) { toast.error("District is required"); return false; }
      if (!formData.pincode) { toast.error("Pincode is required"); return false; }
      if (isEmpty(formData.streetAddress)) {
        toast.error("Street address is required");
        return false;
      }
      if (formData.streetAddress.trim().length < 5) {
        toast.error("Street address must be at least 5 characters");
        return false;
      }
      if (!formData.agreedToTerms) {
        toast.error("You must agree to the terms & conditions");
        return false;
      }
    }

    return true;
  };

  // ---- handlers ----
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormData((p) => ({ ...p, [field]: file }));
    setImagePreview((p) => ({ ...p, [field]: URL.createObjectURL(file) }));
  };

  const removeImage = (field) => {
    if (imagePreview[field]) URL.revokeObjectURL(imagePreview[field]);
    setFormData((p) => ({ ...p, [field]: null }));
    setImagePreview((p) => ({ ...p, [field]: null }));
  };

  // additional social links
  const addLink = () =>
    setFormData((p) => ({ ...p, additionalLinks: [...p.additionalLinks, ""] }));
  const updateLink = (idx, val) =>
    setFormData((p) => {
      const links = [...p.additionalLinks];
      links[idx] = val;
      return { ...p, additionalLinks: links };
    });
  const removeLink = (idx) =>
    setFormData((p) => ({
      ...p,
      additionalLinks: p.additionalLinks.filter((_, i) => i !== idx),
    }));

  // local area typeahead handlers
  const handleLocalAreaInput = (e) => {
    const val = e.target.value;
    setLocalAreaSearch(val);
    setShowLocalSuggestions(true);
    // if user clears the field, reset selection
    if (!val.trim()) {
      setFormData((p) => ({ ...p, localAreaId: "", localAreaKeyword: "" }));
    } else {
      // treat as keyword until user picks from list
      setFormData((p) => ({ ...p, localAreaId: "", localAreaKeyword: val }));
    }
  };

  const selectLocalArea = (area) => {
    setLocalAreaSearch(area.name);
    setShowLocalSuggestions(false);
    setFormData((p) => ({ ...p, localAreaId: area._id, localAreaKeyword: "" }));
  };

  const nextStep = () => {
    if (!validateStep()) return;
    setCurrentStep((s) => Math.min(s + 1, 3));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prevStep = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setLoading(true);

    try {
      if (!accessToken) {
        toast.error("Session expired. Please login again.");
        navigateTo("/merchant-login");
        return;
      }

      // Upload any selected images first — /api/upload/merchant converts each to
      // WebP and returns its public URL; only URLs are sent to /api/merchant/create.
      const uploadedUrls = {};
      await Promise.all(
        IMAGE_FIELDS.map(async (field) => {
          const file = formData[field];
          if (!file) return;
          const ext = file.type?.split("/")[1] || "jpg";
          uploadedUrls[field] = await uploadImageToServer(file, "merchant", `${field}.${ext}`);
        })
      );

      // Social media links
      const allLinks = [
        formData.facebookLink,
        formData.instagramLink,
        ...formData.additionalLinks,
      ].filter((l) => l && l.trim());

      const payload = {
        // Personal
        name: formData.fullName,
        email: formData.email,
        dob: formData.dob,
        ...(formData.aadhaarNumber ? { aadhaarNumber: formData.aadhaarNumber } : {}),
        ...(formData.panNumber ? { panNumber: formData.panNumber } : {}),

        // Business
        businessName: formData.businessName,
        ...(formData.gstinNumber ? { gstinNumber: formData.gstinNumber } : {}),
        businessTypeId: formData.businessType,
        categoryModel: isServiceProvider ? "BusinessService" : "BusinessCategory",
        businessCategoryId: isServiceProvider
          ? formData.businessService
          : formData.businessCategory,
        socialMediaLinks: allLinks,

        // Address
        countryId: formData.country,
        stateId: formData.state,
        districtId: formData.district,
        ...(formData.localAreaId ? { localAreaId: formData.localAreaId } : {}),
        ...(formData.localAreaKeyword ? { localAreaKeyword: formData.localAreaKeyword } : {}),
        pincode: formData.pincode,
        streetAddress: formData.streetAddress,

        // Images (URLs from /api/upload)
        ...uploadedUrls,
      };

      const res = await dispatch(createInquiry(payload)).unwrap();

      if (res?.success) {
        toast.success("Merchant inquiry submitted successfully!");
        navigateTo("/inquiry-status");
      } else {
        toast.error(res?.message || "Submission failed");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to submit inquiry.");
    } finally {
      setLoading(false);
    }
  };

  // ---- KYC doc uploader helper (with crop) ----
  const KycDoc = ({ label, required, field, cropper }) => (
    <div className="kyc-doc-item">
      <div className="doc-label">
        {label} {required && <span className="text-danger">*</span>}
      </div>
      {imagePreview[field] ? (
        <div className="doc-preview-box">
          <img src={imagePreview[field]} alt={label} className="doc-preview-img" />
          {/* Remove button */}
          <button
            type="button"
            className="doc-remove-btn"
            onClick={() => removeImage(field)}
          >
            <X size={11} />
          </button>
          {/* Re-crop / change button */}
          <label className="doc-recrop-btn" title="Change / Re-crop">
            <Camera size={10} />
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) cropper.openCropper(file);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      ) : (
        <label className="doc-upload-area">
          <FileText size={18} className="doc-upload-icon" />
          <span>Upload &amp; Crop</span>
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) cropper.openCropper(file);
              e.target.value = "";
            }}
          />
        </label>
      )}
    </div>
  );

  // ---- Owner photo circle helper ----
  const OwnerPhotoUpload = () => (
    <div className="text-center mb-3">
      <div className="image-upload-container">
        {imagePreview.ownerPhoto ? (
          <div className="uploaded-image-preview">
            <div className="upload-circle-small-preview">
              <img
                src={imagePreview.ownerPhoto}
                alt="Owner"
                className="owner-img-cropped"
              />
              <div className="image-action-overlay">
                <button
                  type="button"
                  className="action-btn remove"
                  onClick={() => removeImage("ownerPhoto")}
                >
                  <X size={16} />
                </button>
                <label className="action-btn change">
                  <Camera size={16} />
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) ownerPhotoCropper.openCropper(file);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        ) : (
          <label className="upload-circle-small">
            <User className="upload-icon-small" />
            <div className="upload-badge-small">
              <Camera size={14} />
            </div>
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) ownerPhotoCropper.openCropper(file);
                e.target.value = "";
              }}
            />
          </label>
        )}
        <div className="upload-label-small">
          {imagePreview.ownerPhoto ? "Owner's Photo Uploaded" : "Upload Owner's Photo *"}
        </div>
      </div>
    </div>
  );

  // ==============================
  return (
    <div className="merchant-form-container">
      <div className="container-fluid h-100">
        <div className="row justify-content-center h-100 align-items-center">
          <div className="col-12 merchant-enquiry d-flex justify-content-center">
            <form className="form-container" onSubmit={handleSubmit}>
              {/* Header */}
              <div className="inquiry-form-header">
                <div className="text-center mb-2">
                  <div className="text-center inquiry-header-icon me-3">
                    <Store />
                    <span className="fs-5 mb-0"> Merchant Inquiry</span>
                    <h6 className="text-light">Join our merchant network</h6>
                  </div>
                </div>
                {/* Progress */}
                <div className="step-indicators">
                  {["Personal", "Business", "Address"].map((label, i) => (
                    <div
                      key={i}
                      className={`step-dot ${currentStep === i + 1 ? "active" : ""} ${currentStep > i + 1 ? "done" : ""}`}
                    >
                      <span className="step-num">{i + 1}</span>
                      <span className="step-label">{label}</span>
                    </div>
                  ))}
                  <div
                    className="step-line"
                    style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                  />
                </div>
              </div>

              <div className="form-card">
                {/* ======================== STEP 1 – PERSONAL ======================== */}
                {currentStep === 1 && (
                  <div className="step-content">
                    <div className="step-header">
                      <h6>Personal Information</h6>
                    </div>

                    <OwnerPhotoUpload />

                    <ImageCropModal
                      cropper={ownerPhotoCropper}
                      onSave={(blob) => {
                        setImagePreview((p) => ({
                          ...p,
                          ownerPhoto: URL.createObjectURL(blob),
                        }));
                        setFormData((p) => ({ ...p, ownerPhoto: blob }));
                      }}
                    />

                    <div className="row">
                      {/* Name – editable */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <User className="input-icon" />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            name="fullName"
                            placeholder="Full Name *"
                            value={formData.fullName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      {/* Email – editable */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Mail className="input-icon" />
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            name="email"
                            placeholder="Email (Optional)"
                            value={formData.email}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      {/* Phone – readonly */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Phone className="input-icon" />
                          <input
                            type="tel"
                            className="form-control form-control-sm"
                            name="contactNumber"
                            value={formData.contactNumber}
                            readOnly
                          />
                        </div>
                      </div>

                      {/* DOB */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Calendar className="input-icon" />
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            name="dob"
                            value={formData.dob}
                            onChange={handleInputChange}
                            max={maxDob}
                            required
                          />
                        </div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>
                          You must be at least 18 years old (DD/MM/YYYY)
                        </small>
                      </div>
                    </div>

                    {/* KYC Section */}
                    <div className="kyc-section mb-2 mt-1">
                      <div className="kyc-heading">
                        <CreditCard size={14} className="me-1" />
                        Aadhaar Card
                        <span className="text-muted ms-1" style={{ fontSize: "0.7rem", fontWeight: 400 }}>
                          (Optional)
                        </span>
                      </div>

                      {/* Aadhaar number text input */}
                      <div className="input-with-icon mb-2">
                        <FileText className="input-icon" />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="aadhaarNumber"
                          placeholder="Enter Aadhaar Number"
                          value={formData.aadhaarNumber}
                          onChange={handleInputChange}
                          maxLength={16}
                        />
                      </div>

                      <div className="kyc-docs-flex">
                        <KycDoc label="Aadhaar Front" field="aadhaarCardFront" cropper={aadhaarFrontCropper} />
                        <KycDoc label="Aadhaar Back"  field="aadhaarCardBack"  cropper={aadhaarBackCropper} />
                      </div>
                    </div>

                    {/* Aadhaar crop modals */}
                    <ImageCropModal
                      cropper={aadhaarFrontCropper}
                      title="Crop Aadhaar Front"
                      aspect={85.6 / 53.98}
                      onSave={(blob) => {
                        setImagePreview((p) => ({ ...p, aadhaarCardFront: URL.createObjectURL(blob) }));
                        setFormData((p) => ({ ...p, aadhaarCardFront: blob }));
                      }}
                    />
                    <ImageCropModal
                      cropper={aadhaarBackCropper}
                      title="Crop Aadhaar Back"
                      aspect={85.6 / 53.98}
                      onSave={(blob) => {
                        setImagePreview((p) => ({ ...p, aadhaarCardBack: URL.createObjectURL(blob) }));
                        setFormData((p) => ({ ...p, aadhaarCardBack: blob }));
                      }}
                    />

                    <div className="kyc-section mb-2">
                      <div className="kyc-heading">
                        <CreditCard size={14} className="me-1" />
                        PAN Card
                        <span className="text-muted ms-1" style={{ fontSize: "0.7rem", fontWeight: 400 }}>
                          (Optional)
                        </span>
                      </div>

                      {/* PAN number text input */}
                      <div className="input-with-icon mb-2">
                        <FileText className="input-icon" />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="panNumber"
                          placeholder="Enter PAN Number (Optional)"
                          value={formData.panNumber}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              panNumber: e.target.value.toUpperCase(),
                            }))
                          }
                          maxLength={10}
                        />
                      </div>

                      <div className="kyc-docs-flex">
                        <KycDoc label="PAN Front" required={false} field="panCardFront" cropper={panFrontCropper} />
                        <KycDoc label="PAN Back"  required={false} field="panCardBack"  cropper={panBackCropper} />
                      </div>
                    </div>

                    {/* PAN crop modals */}
                    <ImageCropModal
                      cropper={panFrontCropper}
                      title="Crop PAN Front"
                      aspect={85.6 / 53.98}
                      onSave={(blob) => {
                        setImagePreview((p) => ({ ...p, panCardFront: URL.createObjectURL(blob) }));
                        setFormData((p) => ({ ...p, panCardFront: blob }));
                      }}
                    />
                    <ImageCropModal
                      cropper={panBackCropper}
                      title="Crop PAN Back"
                      aspect={85.6 / 53.98}
                      onSave={(blob) => {
                        setImagePreview((p) => ({ ...p, panCardBack: URL.createObjectURL(blob) }));
                        setFormData((p) => ({ ...p, panCardBack: blob }));
                      }}
                    />
                  </div>
                )}

                {/* ======================== STEP 2 – BUSINESS ======================== */}
                {currentStep === 2 && (
                  <div className="step-content">
                    <div className="step-header">
                      <h6>Business Information</h6>
                    </div>

                    {/* Business Logo */}
                    <div className="text-center mb-3">
                      <div className="image-upload-container">
                        {imagePreview.businessLogo ? (
                          <div className="uploaded-image-preview">
                            <div className="upload-circle-small-preview">
                              <img
                                src={imagePreview.businessLogo}
                                alt="Business Logo"
                                className="owner-img-cropped"
                              />
                              <div className="image-action-overlay">
                                <button
                                  type="button"
                                  className="action-btn remove"
                                  onClick={() => removeImage("businessLogo")}
                                >
                                  <X size={16} />
                                </button>
                                <label className="action-btn change">
                                  <Camera size={16} />
                                  <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file)
                                        businessLogoCropper.openCropper(file);
                                      e.target.value = "";
                                    }}
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <label className="upload-circle-small">
                            <Building className="upload-icon-small" />
                            <div className="upload-badge-small">
                              <Camera size={14} />
                            </div>
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) businessLogoCropper.openCropper(file);
                                e.target.value = "";
                              }}
                            />
                          </label>
                        )}
                        <div className="upload-label-small">
                          {imagePreview.businessLogo
                            ? "Business Logo Uploaded"
                            : "Upload Business Logo"}
                        </div>
                      </div>
                    </div>

                    <ImageCropModal
                      cropper={businessLogoCropper}
                      onSave={(blob) => {
                        setImagePreview((p) => ({
                          ...p,
                          businessLogo: URL.createObjectURL(blob),
                        }));
                        setFormData((p) => ({ ...p, businessLogo: blob }));
                      }}
                    />

                    <div className="row">
                      <div className="col-12 mb-2">
                        <div className="input-with-icon">
                          <Building className="input-icon" />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            name="businessName"
                            placeholder="Business Name *"
                            value={formData.businessName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <FileText className="input-icon" />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            name="gstinNumber"
                            placeholder="GSTIN Number (Optional)"
                            value={formData.gstinNumber}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      {/* Business Type */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Briefcase className="input-icon" />
                          <select
                            className="form-select form-select-sm"
                            name="businessType"
                            value={formData.businessType}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Business Type</option>
                            {businessTypes.map((type) => (
                              <option key={type._id} value={type._id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Business Category / Service */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Tags className="input-icon" />
                          {isServiceProvider ? (
                            <select
                              className="form-select form-select-sm"
                              name="businessService"
                              value={formData.businessService}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Service</option>
                              {businessServices.map((s) => (
                                <option key={s._id} value={s._id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <select
                              className="form-select form-select-sm"
                              name="businessCategory"
                              value={formData.businessCategory}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="">Select Category</option>
                              {businessCategories.map((c) => (
                                <option key={c._id} value={c._id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Social Media Links */}
                    <div className="social-section mb-2">
                      <div className="kyc-heading">
                        <Link size={14} className="me-1" />
                        Social Media Links
                      </div>

                      {/* Facebook */}
                      <div className="input-with-icon mb-2">
                        <Facebook className="input-icon social-icon-fb" size={18} />
                        <input
                          type="url"
                          className="form-control form-control-sm"
                          name="facebookLink"
                          placeholder="Facebook Page URL (Optional)"
                          value={formData.facebookLink}
                          onChange={handleInputChange}
                        />
                      </div>

                      {/* Instagram */}
                      <div className="input-with-icon mb-2">
                        <Instagram className="input-icon social-icon-ig" size={18} />
                        <input
                          type="url"
                          className="form-control form-control-sm"
                          name="instagramLink"
                          placeholder="Instagram Profile URL (Optional)"
                          value={formData.instagramLink}
                          onChange={handleInputChange}
                        />
                      </div>

                      {/* Additional links */}
                      {formData.additionalLinks.map((link, idx) => (
                        <div className="input-with-icon mb-2" key={idx}>
                          <Link className="input-icon" size={16} />
                          <input
                            type="url"
                            className="form-control form-control-sm"
                            placeholder={`Other Social Link ${idx + 1} (Optional)`}
                            value={link}
                            onChange={(e) => updateLink(idx, e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn-remove-extra-link"
                            onClick={() => removeLink(idx)}
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn-add-link"
                        onClick={addLink}
                      >
                        <Plus size={14} className="me-1" />
                        Add more social link
                      </button>
                    </div>
                  </div>
                )}

                {/* ======================== STEP 3 – ADDRESS ======================== */}
                {currentStep === 3 && (
                  <div className="step-content">
                    <div className="step-header">
                      <h6>Address Information</h6>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Globe className="input-icon" />
                          <select
                            className="form-control"
                            name="country"
                            value={formData.country}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Country *</option>
                            {countries.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Map className="input-icon" />
                          <select
                            className="form-control"
                            name="state"
                            value={formData.state}
                            onChange={handleInputChange}
                            required
                            disabled={!formData.country}
                          >
                            <option value="">Select State *</option>
                            {states.map((s) => (
                              <option key={s._id} value={s._id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <Building className="input-icon" />
                          <select
                            className="form-control"
                            name="district"
                            value={formData.district}
                            onChange={handleInputChange}
                            required
                            disabled={!formData.state}
                          >
                            <option value="">Select District *</option>
                            {districts.map((d) => (
                              <option key={d._id} value={d._id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Local Area – optional typeahead */}
                      <div className="col-12 col-md-6 mb-2" ref={localAreaRef}>
                        <div className="input-with-icon" style={{ position: "relative" }}>
                          <Search className="input-icon" size={16} />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Local Area (Optional – type to search or add)"
                            value={localAreaSearch}
                            onChange={handleLocalAreaInput}
                            onFocus={() => setShowLocalSuggestions(true)}
                            disabled={!formData.district}
                          />
                          {localAreaSearch && (
                            <button
                              type="button"
                              className="local-area-clear"
                              onClick={() => {
                                setLocalAreaSearch("");
                                setFormData((p) => ({
                                  ...p,
                                  localAreaId: "",
                                  localAreaKeyword: "",
                                }));
                              }}
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>
                        {/* Suggestions dropdown */}
                        {showLocalSuggestions &&
                          localAreaSearch &&
                          filteredLocalAreas.length > 0 && (
                            <div className="local-area-dropdown">
                              {filteredLocalAreas.slice(0, 8).map((area) => (
                                <div
                                  key={area._id}
                                  className="local-area-option"
                                  onMouseDown={() => selectLocalArea(area)}
                                >
                                  <MapPin size={12} className="me-1" />
                                  {area.name}
                                </div>
                              ))}
                            </div>
                          )}
                        {/* Custom keyword hint */}
                        {formData.localAreaKeyword && !formData.localAreaId && (
                          <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                            Using custom area: "{formData.localAreaKeyword}"
                          </small>
                        )}
                        {formData.localAreaId && (
                          <small className="text-success" style={{ fontSize: "0.7rem" }}>
                            Area selected from list
                          </small>
                        )}
                      </div>

                      {/* Pincode */}
                      <div className="col-12 col-md-6 mb-2">
                        <div className="input-with-icon">
                          <MapPin className="input-icon" />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            name="pincode"
                            placeholder="Pincode *"
                            required
                            value={formData.pincode}
                            onChange={handleInputChange}
                            maxLength={10}
                          />
                        </div>
                      </div>

                      {/* Street Address */}
                      <div className="col-12 mb-2">
                        <div className="input-with-icon">
                          <MapPin className="input-icon" />
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            name="streetAddress"
                            placeholder="Street Address *"
                            value={formData.streetAddress}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="terms-block mt-2 mb-3">
                      <label className="terms-label">
                        <input
                          type="checkbox"
                          name="agreedToTerms"
                          checked={formData.agreedToTerms}
                          onChange={handleInputChange}
                          className="terms-checkbox"
                        />
                        <span>
                          I agree to the{" "}
                          <a href="/terms" target="_blank" rel="noreferrer">
                            Terms &amp; Conditions
                          </a>{" "}
                          and submit this application
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="form-navigation mt-auto">
                  <div className="d-flex justify-content-between align-items-center">
                    {currentStep > 1 ? (
                      <button
                        type="button"
                        onClick={prevStep}
                        className="btn btn-outline-primary btn-sm"
                      >
                        <ArrowLeft size={15} className="me-1" /> Back
                      </button>
                    ) : (
                      <span />
                    )}

                    {currentStep < 3 ? (
                      <button
                        type="button"
                        onClick={nextStep}
                        className="btn btn-primary btn-sm ms-auto"
                      >
                        Next <ArrowRight size={15} className="ms-1" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={loading || inquiryState.loading || !formData.agreedToTerms}
                        className="btn btn-primary btn-sm ms-auto"
                      >
                        {loading || inquiryState.loading ? (
                          "Submitting..."
                        ) : (
                          <>
                            <Send size={14} className="me-1" /> Submit Application
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        :root {
          --color-primary: #6052a5;
          --color-primary-dark: #4a3f85;
          --color-primary-light: #4b3d9a;
          --color-secondary: #e5e7eb;
          --color-text-dark: #1f2937;
          --color-text-light: #6b7280;
          --color-background-light: #f9fafb;
          --color-danger: #ef4444;
          --color-border: #d1d5db;
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          --shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.12);
        }

        .merchant-form-container {
          min-height: 100vh;
          background: radial-gradient(circle at top right, rgba(96, 82, 165, 0.15), transparent 40%),
            radial-gradient(circle at bottom left, rgba(99, 102, 241, 0.12), transparent 40%),
            linear-gradient(180deg, #f9fafb, #ffffff);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1rem;
        }

        .merchant-enquiry {
          max-width: 600px;
        }

        /* ======= HEADER ======= */
        .inquiry-form-header {
          background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
          padding: 1rem 1.25rem 1.25rem;
          color: white;
          position: relative;
        }

        .inquiry-header-icon {
          background: rgba(255, 255, 255, 0.25);
          padding: 2px;
          border-radius: 14px;
        }

        .inquiry-header-icon svg {
          color: white;
        }

        /* ======= STEP INDICATORS ======= */
        .step-indicators {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1.25rem;
          position: relative;
        }

        .step-indicators::before {
          content: "";
          position: absolute;
          top: 14px;
          left: 0;
          right: 0;
          height: 2px;
          background: rgba(255, 255, 255, 0.3);
          z-index: 0;
        }

        .step-line {
          position: absolute;
          top: 14px;
          left: 0;
          height: 2px;
          background: white;
          z-index: 1;
          transition: width 0.5s ease;
        }

        .step-dot {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          z-index: 2;
        }

        .step-num {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          color: white;
          font-size: 0.8rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid rgba(255, 255, 255, 0.5);
          transition: var(--transition);
        }

        .step-label {
          font-size: 0.68rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .step-dot.active .step-num {
          background: white;
          color: var(--color-primary);
          border-color: white;
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
        }

        .step-dot.done .step-num {
          background: rgba(255, 255, 255, 0.9);
          color: var(--color-primary);
        }

        /* ======= FORM CARD ======= */
        .form-container {
          background: rgba(255, 255, 255, 0.97);
          border-radius: 22px;
          box-shadow: var(--shadow);
          border: 1px solid rgba(255, 255, 255, 0.6);
          overflow: hidden;
          transition: var(--transition);
          width: 100%;
          max-width: 100%;
        }

        .form-card {
          padding: 2rem 1.75rem 1.5rem;
          min-height: 460px;
          display: flex;
          flex-direction: column;
        }

        .step-content {
          animation: fadeIn 0.35s ease-out;
          flex: 1;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .step-header h6 {
          font-weight: 700;
          color: var(--color-primary);
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }

        /* ======= INPUTS ======= */
        .input-with-icon {
          position: relative;
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          width: 18px;
          height: 18px;
          color: var(--color-text-light);
          pointer-events: none;
          z-index: 1;
        }

        .social-icon-fb { color: #1877f2 !important; }
        .social-icon-ig { color: #e1306c !important; }

        .form-control,
        .form-select {
          padding-left: 44px !important;
          height: 44px;
          border-radius: 12px;
          border: 1.5px solid var(--color-secondary);
          font-size: 0.88rem;
          background: var(--color-background-light);
          transition: var(--transition);
          width: 100%;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: var(--color-primary);
          background: white;
          box-shadow: 0 0 0 3px rgba(96, 82, 165, 0.12);
          outline: none;
        }

        .form-control:disabled,
        .form-select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ======= OWNER PHOTO ======= */
        .image-upload-container {
          margin-bottom: 0.5rem;
        }

        .upload-circle-small {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: linear-gradient(145deg, #f9fafb, #eef2ff);
          border: 2px dashed #c7d2fe;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          cursor: pointer;
          position: relative;
          transition: var(--transition);
          overflow: hidden;
        }

        .upload-circle-small:hover {
          background: #eef2ff;
          border-color: var(--color-primary);
          transform: scale(1.03);
        }

        .upload-icon-small {
          color: var(--color-text-light);
          width: 32px;
          height: 32px;
        }

        .upload-badge-small {
          position: absolute;
          bottom: 2px;
          right: 2px;
          background: var(--color-primary);
          color: white;
          padding: 5px;
          border-radius: 50%;
          border: 2px solid white;
        }

        .upload-label-small {
          margin-top: 6px;
          font-size: 0.75rem;
          color: var(--color-text-light);
          font-weight: 500;
        }

        .upload-circle-small-preview {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          overflow: hidden;
          position: relative;
          margin: 0 auto;
          border: 2px solid #6366f1;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        }

        .owner-img-cropped {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .image-action-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .upload-circle-small-preview:hover .image-action-overlay {
          opacity: 1;
        }

        .action-btn {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .action-btn.remove { color: #ff4d4f; }
        .action-btn.change { color: #6366f1; }

        /* ======= KYC SECTION ======= */
        .kyc-section {
          border: 1.5px dashed #c7d2fe;
          border-radius: 12px;
          padding: 0.75rem;
          background: #f5f7ff;
        }

        .kyc-heading {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--color-primary);
          display: flex;
          align-items: center;
          margin-bottom: 0.6rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .kyc-docs-flex {
          display: flex;
          gap: 8px;
          flex-wrap: nowrap;
        }

        .kyc-doc-item {
          flex: 1;
          min-width: 0;
        }

        .doc-label {
          font-size: 0.68rem;
          font-weight: 600;
          color: var(--color-text-light);
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .doc-upload-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          border: 1.5px dashed #c7d2fe;
          border-radius: 8px;
          padding: 8px 4px;
          cursor: pointer;
          background: white;
          transition: var(--transition);
          font-size: 0.68rem;
          color: var(--color-text-light);
          text-align: center;
          height: 68px;
          width: 100%;
        }

        .doc-upload-area:hover {
          border-color: var(--color-primary);
          background: #eef2ff;
          color: var(--color-primary);
        }

        .doc-upload-icon {
          color: var(--color-primary);
          opacity: 0.7;
        }

        .doc-preview-box {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          height: 68px;
          border: 1.5px solid #c7d2fe;
          background: white;
        }

        .doc-preview-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .doc-remove-btn {
          position: absolute;
          top: 3px;
          right: 3px;
          background: rgba(255, 255, 255, 0.92);
          border: none;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #ef4444;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        }

        /* Re-crop / change button — bottom-left of preview */
        .doc-recrop-btn {
          position: absolute;
          bottom: 3px;
          left: 3px;
          background: rgba(96, 82, 165, 0.9);
          border: none;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #ffffff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        }

        .doc-recrop-btn:hover {
          background: var(--color-primary);
        }

        @media (max-width: 480px) {
          .kyc-docs-flex {
            flex-wrap: wrap;
          }
          .kyc-doc-item {
            flex: 0 0 calc(50% - 4px);
          }
        }

        /* ======= SOCIAL MEDIA ======= */
        .social-section {
          border: 1.5px dashed #c7d2fe;
          border-radius: 12px;
          padding: 0.75rem;
          background: #f5f7ff;
        }

        .btn-add-link {
          display: flex;
          align-items: center;
          background: none;
          border: 1.5px dashed var(--color-primary);
          border-radius: 8px;
          padding: 5px 12px;
          color: var(--color-primary);
          font-size: 0.78rem;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition);
          margin-top: 4px;
        }

        .btn-add-link:hover {
          background: #eef2ff;
        }

        .btn-remove-extra-link {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #ef4444;
          cursor: pointer;
          padding: 2px;
          display: flex;
          align-items: center;
          z-index: 2;
        }

        /* ======= LOCAL AREA DROPDOWN ======= */
        .local-area-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1.5px solid #c7d2fe;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          z-index: 999;
          max-height: 200px;
          overflow-y: auto;
        }

        .local-area-option {
          padding: 8px 14px;
          font-size: 0.85rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          color: var(--color-text-dark);
          transition: background 0.15s;
        }

        .local-area-option:hover {
          background: #eef2ff;
          color: var(--color-primary);
        }

        .local-area-clear {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: var(--color-text-light);
          cursor: pointer;
          padding: 2px;
          display: flex;
          align-items: center;
          z-index: 2;
        }

        /* ======= TERMS ======= */
        .terms-block {
          border: 1.5px solid #c7d2fe;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          background: #f5f7ff;
        }

        .terms-label {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
          font-size: 0.85rem;
          color: var(--color-text-dark);
          margin: 0;
          font-weight: 500;
        }

        .terms-checkbox {
          width: 16px;
          height: 16px;
          margin-top: 2px;
          accent-color: var(--color-primary);
          cursor: pointer;
          flex-shrink: 0;
        }

        .terms-label a {
          color: var(--color-primary);
          font-weight: 600;
          text-decoration: underline;
        }

        /* ======= NAVIGATION ======= */
        .form-navigation {
          margin-top: auto;
          padding-top: 1.5rem;
          border-top: 1px solid var(--color-secondary);
        }

        .btn-primary {
          background: linear-gradient(
            135deg,
            var(--color-primary),
            var(--color-primary-light)
          );
          border: none;
          padding: 10px 24px;
          font-weight: 600;
          border-radius: 12px;
          transition: var(--transition);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(96, 82, 165, 0.35);
        }

        .btn-primary:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .btn-outline-primary {
          border: 1.5px solid var(--color-primary);
          padding: 10px 22px;
          font-weight: 600;
          border-radius: 12px;
          color: var(--color-primary);
          background: transparent;
        }

        .btn-outline-primary:hover {
          background: var(--color-primary);
          color: white;
        }

        /* ======= LAYOUT ======= */
        .row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -8px;
        }

        .col-12 { padding: 0 8px; width: 100%; }

        @media (min-width: 768px) {
          .col-md-6 { flex: 0 0 50%; max-width: 50%; }
          .form-container { max-width: 560px; }
        }

        @media (min-width: 992px) {
          .form-container { max-width: 760px; }
        }

        @media (max-width: 576px) {
          .merchant-form-container { padding: 0; }
          .form-container { border-radius: 0; min-height: 100vh; }
          .inquiry-form-header { padding: 1.25rem; }
          .form-card { padding: 1.25rem; }
          .btn { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default MerchantInquiryForm;
